import { schema, normalize } from 'normalizr';
import pick from 'lodash/pick';
import get from 'lodash/get';
import has from 'lodash/has';
import merge from 'lodash/fp/merge';
import { camelizeKeys } from 'humps';

import { parseDbString } from 'utils/textFormats';
import { transformEvent } from 'common/entities/event/utils';

// Constants
export const ENTITIES_KEY_EVENT = 'events';

export const SET_EVENTS = 'entities/SET_EVENTS';
export const MERGE_EVENTS = 'entities/MERGE_EVENTS';
export const UPDATE_EVENT_WORKFLOW = 'entities/UPDATE_EVENT_WORKFLOW';

const VIEW_URL_BASE = '/sites/view/';

// Schemas

export const EVENT_DEFAULTS = {
    emailTemplateCount: 0,
    eventStageCount: 0,
    imageUrl: '', // dont have fallback yet
    eventUrl: null,
    isEditable: false,
};

export const VALID_EVENT_KEYS = [
    'attendanceTypes',
    'id',
    'title',
    'description',
    'organization',
    'created',
    'eventStart',
    'eventEnd',
    'startTime',
    'endTime',
    'venueName',
    'address',
    'city',
    'state',
    'zipCode',
    'country',
    'createdAt',
    'modifiedAt',
    'paidForDomain',
    'currency',
    'multiCurrencyOptions',
    'customDomain',
    'deleted',
    'domain',
    'fqUrl',
    'isRsvpOpen',
    'eventCardImages',
    'previewUrl',
    'editUrl',
    'stats',
    'eventType',
    'eventOwnerFirstName',
    'eventOwnerLastName',
    'eventOwnerEmail',
    'venueTbd',
    'isTheme',
    'isHub',
    'whiteLabel',
    'autosave',
    'published',
    'eventWorkflow',
    'cachedEventWorkflowStatus',
    'duplicationAllowed',
    'organization',
    'multisession',
    'hideFooter',
    'isRegistrationUpdatingEnabled',
    'hasTaxes',
    'isStripePayoutMethod',
    'isAiSiteDesigner',
    'isFormV3',
];

export const isValidEventKey = (key) => VALID_EVENT_KEYS.indexOf(key) !== -1;

// Need to remove everything not being used!!! Saving space
// -- do the same in themes. Also, move theme fetch so it doesnt happen until themes dash opened
export const processEvent = (eventData) => {
    const camelizedEventData = camelizeKeys(eventData);
    const parsedEventData = pick(camelizedEventData, VALID_EVENT_KEYS);

    const rsvpMethodValue =
        get(camelizedEventData, 'rsvpMethod', null) || get(camelizedEventData, 'eventSetting.rsvpMethod', null);
    const rsvpMethod = rsvpMethodValue ? { rsvpMethod: rsvpMethodValue } : {};

    return {
        ...camelizedEventData.eventSetting,
        /* Important to have the eventSetting data above the parsedEventData,
         * lest the eventSetting id overwrite the event id. */
        ...parsedEventData,
        ...rsvpMethod,
        customDomain: camelizedEventData.customDomain || null,
        isHub: !!parsedEventData.isHub || !!eventData.hub,
        /* Only set isAiSiteDesigner when the payload carries it: partial updates (updateEvent) merge into the stored
         * event, and defaulting it to false there would silently turn an AI event into a CMS event. */
        ...(has(parsedEventData, 'isAiSiteDesigner') ? { isAiSiteDesigner: !!parsedEventData.isAiSiteDesigner } : {}),
        id: parsedEventData.id.toString(), // Event id should always be a string
        eventCard: get(camelizedEventData, 'eventSetting.eventCards.x2', null),
        lat: get(camelizedEventData, 'eventSetting.lat', null),
        lng: get(camelizedEventData, 'eventSetting.lng', null),
        title: camelizedEventData.title && parseDbString(camelizedEventData.title),
        venueName: camelizedEventData.venueName && parseDbString(camelizedEventData.venueName),
        city: camelizedEventData.city && parseDbString(camelizedEventData.city),
        address: camelizedEventData.address && parseDbString(camelizedEventData.address),
        eventType: get(camelizedEventData, 'eventType.name', null),
        eventUrl:
            camelizedEventData.fqUrl && camelizedEventData.fqUrl.indexOf('splashthat')
                ? camelizedEventData.fqUrl.replace('https://', VIEW_URL_BASE)
                : camelizedEventData.fqUrl,
        themeName: camelizedEventData.splashTheme && parseDbString(camelizedEventData.splashTheme.name),
        pagePrivacyType: get(camelizedEventData, 'eventSetting.pagePrivacyType', null),
        whiteLabel: parseInt(parsedEventData.whiteLabel, 10),
        ...(parsedEventData.organization
            ? {
                  organization: {
                      id: parseInt(get(parsedEventData, 'organization.id'), 10),
                  },
              }
            : {}),
    };
};

export const event = new schema.Entity(
    ENTITIES_KEY_EVENT,
    {},
    {
        processStrategy: (entity) => processEvent(entity),
    },
);

export const eventList = [event];

const normalizeEvents = (events) => normalize(events, eventList);

const getEntitiesToMerge = (normalizedEvents) =>
    merge(
        {
            entities: {
                [ENTITIES_KEY_EVENT]: {
                    allIds: normalizedEvents.result,
                },
            },
        },
        normalizedEvents,
    );

// Actions
export const setEvents = (events) => ({
    type: SET_EVENTS,
    payload: getEntitiesToMerge(normalizeEvents(events)),
});

export const mergeEvents = (events) => ({
    type: MERGE_EVENTS,
    payload: getEntitiesToMerge(normalizeEvents(events)),
});

export const updateEvent = (eventId, updatedEvent) =>
    mergeEvents([
        transformEvent({
            id: eventId,
            ...updatedEvent,
        }),
    ]);
